import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { I18n } from '@aws-amplify/core';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Row, Col, Form, Skeleton, notification } from 'antd';

import { CreateBeraJobSummaryRequest, UpdateBeraJobSummaryRequest } from './hooks/types/request';
import { BeraJobStepsRouteParams, useBeraJobStepsContext } from './context';
import { useApplicationContext } from '@/context/v1/Application/context';
import { BeraReportStepsProvider } from './BeraFormSteps/context';
import { GeneralInformationsForm, HeaderRow, FooterRow } from './styles';
import { TaskTimeFormat } from './hooks/types/response';
import { useJobSummaryFiles } from './hooks/useJobSummaryFiles';
import { useUpdateBeraJobSummaryFile } from './hooks/useBeraReportV2';
import { FilesSelection } from './FilesSelection';
import { PartialSelection } from './FilesSelection/PartialSelection';
import { usePartialRevision } from './FilesSelection/PartialSelection/usePartialRevision';
import { BeraFormSteps } from './BeraFormSteps';
import { BeraReportV2 } from './BeraReportV2';
import { FinalResult } from './BeraReportV2/FinalResult';
import { BERA_REPORT_VERSION } from './constants';
import { ModalRevision } from '@/views/Bera/_components/ModalRevision';
import { useGetBeraRevisionSource } from '@/hooks';
import { Informations } from './Informations';
import { BackButton } from './BackButton';
import { Header } from './Header';
import { Footer } from './Footer';
import { BERA_LIST_PATH } from '@/views/Bera/constants';
import {
	BERA_REVISION_TYPE,
	type BeraRevisionOverrides,
	type BeraRevisionSource,
	type BeraRevisionType
} from '@/types/Bera';
import {
	useBeraFiles,
	useCustomReport,
	useBeraJobSummary,
	useCreateBeraJobSummary,
	useUpdateBeraJobSummary,
	useBindFilesToBeraJobSummary
} from './hooks/hooks';

const { useForm, useWatch } = Form;

const REPORT_NAME = 'bera';
const REPORT_TYPE = 'BERA';
const REVISION_QUERY_PARAM = 'revision';
const REVISION_DETECTED_DESCRIPTION =
	'A B.E.R.A. evaluation already exists for this hierarchy. To continue, select the revision type';
const REVISION_TYPE_DESCRIPTION = 'To continue, select the revision type';
const PARTIAL_REVISION_FLAG = BERA_REVISION_TYPE.PARTIAL.toLowerCase();

type RevisionDetection = {
	source: BeraRevisionSource;
	overrides: BeraRevisionOverrides;
};

type CreateOrUpdate = {
	cycle_id: string;
	report_name: string;
	company_id: string;
	evaluator_id?: string;
	operator_evaluated?: string;
	work_center?: string;
};

export type StepsInitialValues = {
	[key: string]: {
		file_id: string;
		task_id: string;
		task_time: number;
		task_time_format: TaskTimeFormat;
		has_known_injury: boolean;
		collection_date: moment.Moment;
	};
};

export function BeraJobSteps(): JSX.Element {
	const [form] = useForm();
	const isJustCreated = useRef<boolean>(false);
	const { organization, company } = useApplicationContext();
	const history = useHistory();
	const { search, pathname } = useLocation();
	const { bera_job_summary_id } = useParams<BeraJobStepsRouteParams>();
	const { selectedFiles, selectedFilesId, showBeraJobResult, beraJobSummaryId, page, limit, fileName, fileFilters } =
		useBeraJobStepsContext();
	const {
		handleBeraFilesChange,
		handleSelectedFilesChange,
		handleBeraJobSummaryChange,
		handleBeraJobSummaryIdChange,
		handleShowBeraJobResultChange,
		handleBeraCommonInformationsChange,
		handleCurrentFileIdChange,
		isFileSelectionPage
	} = useBeraJobStepsContext();

	const [valuesChanged, setValuesChanged] = useState<boolean>(false);
	const [hasAutoOpenedResult, setHasAutoOpenedResult] = useState<boolean>(false);
	const [step, setStep] = useState<number>(0);
	const [revisionDetection, setRevisionDetection] = useState<RevisionDetection | null>(null);

	const revisionFlag = new URLSearchParams(search).get(REVISION_QUERY_PARAM);

	const companyId: string = useWatch('company_id', { form, preserve: true }) ?? company?.id;

	const { data: customReport, isError: errorGettingCustomReport } = useCustomReport({
		organization_id: organization?.id,
		company_id: company?.id,
		reportName: REPORT_NAME
	});

	const {
		data: beraFiles,
		isLoading: gettingBeraFiles,
		isFetching: fetchingBeraFiles,
		isError: errorGettingBerafiles
	} = useBeraFiles({
		company_id: companyId,
		bera_job_summary_id: bera_job_summary_id ?? beraJobSummaryId,
		offset: page === 0 ? page : page - 1,
		custom_report_id: customReport?.id,
		organization_id: organization?.id,
		report_type: REPORT_TYPE,
		fileName,
		limit,
		...fileFilters
	});

	const {
		data: jobSummary,
		isLoading,
		isError
	} = useBeraJobSummary({
		organization_id: organization?.id,
		company_id: companyId,
		bera_job_summary_id: bera_job_summary_id ?? beraJobSummaryId
	});

	const { mutateAsync: createJobSummary, isLoading: creatingJobSummary } = useCreateBeraJobSummary();
	const { mutateAsync: updateJobSummary, isLoading: updatingJobSummary } = useUpdateBeraJobSummary();
	const { mutateAsync: bindFilesToBeraJobSummary, isLoading: bindingFilesToBeraJobSummary } =
		useBindFilesToBeraJobSummary();
	const { mutateAsync: updateBeraJobSummaryFile, isLoading: updatingBeraJobSummaryFile } =
		useUpdateBeraJobSummaryFile();
	const { mutateAsync: findRevisionSource, isLoading: findingRevisionSource } = useGetBeraRevisionSource();

	const isRevision = !!jobSummary?.is_revision;
	const isLegacyVersion = jobSummary?.version === BERA_REPORT_VERSION.V1;
	const isRevisionWithoutFile = isRevision && !jobSummary?.file_id;
	const isPartialRevisionPending = isRevisionWithoutFile && revisionFlag === PARTIAL_REVISION_FLAG;
	const isMissingRevisionType = isRevisionWithoutFile && !revisionFlag;

	const partialRevision = usePartialRevision({
		isEnabled: isRevision,
		organizationId: organization?.id,
		companyId,
		beraJobSummaryId: bera_job_summary_id ?? beraJobSummaryId
	});

	const isPartialRevision = isPartialRevisionPending || partialRevision.isPatched;

	useJobSummaryFiles({
		jobSummary,
		customReportId: customReport?.id,
		selectedFiles,
		selectedFilesId,
		form,
		step,
		handleSelectedFilesChange,
		handleShowBeraJobResultChange,
		handleCurrentFileIdChange,
		setStep
	});

	useEffect(() => {
		handleBeraFilesChange(beraFiles);
		handleBeraJobSummaryChange(jobSummary);
		if (step === 1) {
			const fileIds = selectedFiles?.map((file) => file.id);
			form.setFieldValue('files_list', fileIds);
		}
	}, [step, beraFiles]);

	useEffect(() => {
		if (!jobSummary?.id) {
			return;
		}

		if (isJustCreated.current) {
			isJustCreated.current = false;
			return;
		}

		form.resetFields();
	}, [jobSummary?.id]);

	useEffect(() => {
		if (!jobSummary?.file_id || !revisionFlag) {
			return;
		}

		history.replace(pathname);
	}, [history, pathname, revisionFlag, jobSummary?.file_id]);

	useEffect(() => {
		if (!beraJobSummaryId && bera_job_summary_id) {
			handleBeraJobSummaryIdChange(bera_job_summary_id);
		}
	}, [beraJobSummaryId, bera_job_summary_id]); // eslint-disable-line react-hooks/exhaustive-deps

	const generalInformationsInitialValues = {
		organization_id: organization?.name,
		company_id: jobSummary?.company_id ?? company?.id,
		report_name: jobSummary?.report_name,
		cycle_id: jobSummary?.cycle_id,
		sector_id: jobSummary?.sector_id,
		workstation_id: jobSummary?.workstation_id,
		line_id: jobSummary?.line_id,
		evaluator_id: jobSummary?.evaluator_id ?? undefined,
		operator_evaluated: jobSummary?.operator_evaluated ?? undefined,
		work_center: jobSummary?.work_center ?? undefined
	};

	function handleValueChange(changed: boolean): void {
		setValuesChanged(changed);
	}

	function handlePreviousStep(): void {
		setStep(step - 1);
	}

	async function persistFileSelection(): Promise<void> {
		const summaryId = bera_job_summary_id ?? beraJobSummaryId;

		if (jobSummary?.version === BERA_REPORT_VERSION.V2) {
			const [fileId] = selectedFilesId;
			const params = {
				organization_id: organization.id,
				company_id: companyId,
				bera_job_summary_id: summaryId,
				file_id: String(fileId),
				sector_id: form.getFieldValue('sector_id'),
				workstation_id: form.getFieldValue('workstation_id')
			};

			await updateBeraJobSummaryFile(params);
			return;
		}

		const params = {
			bera_job_summary_id: summaryId,
			file_ids: selectedFilesId,
			organization_id: organization.id,
			company_id: companyId
		};

		await bindFilesToBeraJobSummary(params);
	}

	function selectPatchedFile(patchedFileId: string): void {
		const rows = beraFiles?.files?.rows ?? [];
		const [replacementFileId] = partialRevision.selectedFileIds;
		const replacement = rows.find((file) => String(file.id) === replacementFileId);

		if (!replacement) {
			return;
		}

		const patchedFile = { ...replacement, id: patchedFileId };

		handleSelectedFilesChange([patchedFile]);
	}

	async function handleNextStep() {
		try {
			if (isPartialRevision && step === mainSteps.length - 1) {
				if (partialRevision.isPatched) {
					setStep(step + 1);
					return;
				}

				const patchedFileId = await partialRevision.submitPartialRevision();

				if (patchedFileId) {
					selectPatchedFile(patchedFileId);
					setStep(step + 1);
				}

				return;
			}
			if (jobSummary.show_bera_result && step === mainSteps.length - 1) {
				if (isFileSelectionPage) {
					await persistFileSelection();
				}
				setStep(step + 1);
				return;
			}
			const values = await form.validateFields();
			if (step === mainSteps.length - 1) {
				if (isFileSelectionPage) {
					await persistFileSelection();
				}
				const values = await form.getFieldValue([]);
				if (values) {
					handleBeraCommonInformationsChange(values);
					setStep(step + 1);
				}
				return;
			}
			if (step === 0) {
				const { cycle_id, report_name, company_id, evaluator_id, operator_evaluated, work_center } = values;
				const generalInformations = {
					cycle_id,
					report_name,
					company_id,
					evaluator_id,
					operator_evaluated,
					work_center
				};

				return await createOrUpdateBeraJob(generalInformations);
			}
			setStep(step + 1);
		} catch (error: any) {
			const hasFormErrors = !!error?.errorFields?.length;

			if (!hasFormErrors) {
				return;
			}

			const errorName = error.errorFields[0].name;
			form.scrollToField(errorName, { behavior: 'smooth', block: 'center', inline: 'center' });
			notification.error({
				message: I18n.get('Ops... something happened!'),
				description: I18n.get("Some required steps wasn't filled"),
				duration: 5
			});
		}
	}

	async function createOrUpdateBeraJob(values: CreateOrUpdate) {
		if (!valuesChanged) {
			setStep(step + 1);
			return;
		}

		if (!beraJobSummaryId && !bera_job_summary_id) {
			return await createBeraJobSummary(values);
		}

		return await updateBeraJobSummary(values);
	}

	async function createBeraJobSummary(values: CreateOrUpdate): Promise<void> {
		const params = {
			organization_id: organization.id,
			company_id: values.company_id,
			cycle_id: values.cycle_id,
			workstation_id: form.getFieldValue('workstation_id')
		};

		const source = await findRevisionSource(params);

		if (source) {
			const overrides = {
				report_name: values.report_name,
				evaluator_id: values.evaluator_id,
				operator_evaluated: values.operator_evaluated,
				work_center: values.work_center
			};

			setRevisionDetection({ source, overrides });
			return;
		}

		const body: CreateBeraJobSummaryRequest = {
			...values,
			organization_id: organization.id
		};

		const createdJobSummary = await createJobSummary(body);

		if (createdJobSummary) {
			isJustCreated.current = true;
			handleBeraJobSummaryIdChange(createdJobSummary.id);
			setStep(step + 1);
			updateURI(createdJobSummary.id);
		}
	}

	async function updateBeraJobSummary(values: CreateOrUpdate): Promise<void> {
		const body: UpdateBeraJobSummaryRequest = {
			...values,
			id: beraJobSummaryId || bera_job_summary_id,
			organization_id: organization.id
		};

		const updatedJobSummary = await updateJobSummary(body);

		if (updatedJobSummary) {
			handleBeraJobSummaryIdChange(updatedJobSummary.id);
			setStep(step + 1);
			updateURI(updatedJobSummary.id);
		}
	}

	function updateURI(id: string) {
		if (bera_job_summary_id) {
			return;
		}

		const currentPath = window.location.pathname;
		const pathParts = currentPath.split('/');
		const lastPart = pathParts[pathParts.length - 1];

		if (lastPart === id) {
			return;
		}

		const newurl =
			window.location.protocol +
			'//' +
			window.location.host +
			window.location.pathname +
			`/${id}` +
			window.location.search;
		window.history.pushState({ path: newurl }, '', newurl);
	}

	function handleCloseRevisionDetection(): void {
		setRevisionDetection(null);
	}

	function handleSelectRevisionType(revisionType: BeraRevisionType): void {
		const flag = revisionType.toLowerCase();

		history.replace(`${pathname}?${REVISION_QUERY_PARAM}=${flag}`);
	}

	function handleCloseRevisionType(): void {
		history.push(BERA_LIST_PATH);
	}

	function handleHideBeraJobResult(): void {
		handleShowBeraJobResultChange(false);
	}

	function handleAutoOpenResult(): void {
		setHasAutoOpenedResult(true);
		handleShowBeraJobResultChange(true);
	}

	function renderReportContent(): JSX.Element {
		const isVersionTwo = jobSummary?.version === BERA_REPORT_VERSION.V2;
		const [selectedFile] = selectedFiles;

		if (isVersionTwo && selectedFile) {
			const summary = { ...jobSummary, id: jobSummary?.id ?? (bera_job_summary_id || beraJobSummaryId) };

			if (showBeraJobResult) {
				return <FinalResult jobSummary={summary} file={selectedFile} onBack={handleHideBeraJobResult} />;
			}

			return (
				<BeraReportV2
					file={selectedFile}
					jobSummary={summary}
					autoOpenResult={!hasAutoOpenedResult}
					onPrevious={handlePreviousStep}
					onAutoOpenResult={handleAutoOpenResult}
				/>
			);
		}

		return (
			<BeraReportStepsProvider>
				<BeraFormSteps />
			</BeraReportStepsProvider>
		);
	}

	function showBeraReportStep(): JSX.Element {
		const loadingFiles = fetchingBeraFiles && jobSummary.show_bera_result;

		if (
			isLoading ||
			loadingFiles ||
			creatingJobSummary ||
			findingRevisionSource ||
			updatingJobSummary ||
			bindingFilesToBeraJobSummary ||
			updatingBeraJobSummaryFile ||
			partialRevision.isPatching
		) {
			return (
				<Col span={16}>
					<Skeleton active />
				</Col>
			);
		}

		if (step === 2) {
			return (
				<Col xs={24}>
					{step === mainSteps.length && !showBeraJobResult && (
						<BackButton onPreviousClick={handlePreviousStep} />
					)}
					{renderReportContent()}
				</Col>
			);
		}

		return (
			<Col xs={24}>
				<GeneralInformationsForm
					autoComplete="off"
					name="general_informations"
					form={form}
					initialValues={generalInformationsInitialValues}
				>
					{mainSteps[step].component}
				</GeneralInformationsForm>
			</Col>
		);
	}

	function isNextButtonDisabled(): boolean {
		if (isPartialRevision) {
			return false;
		}

		return step === 1 && selectedFiles.length === 0 && selectedFilesId.length === 0;
	}

	function buildFileSelectionStep(): JSX.Element {
		const isLoadingFiles = gettingBeraFiles || fetchingBeraFiles;

		if (isPartialRevision) {
			return (
				<PartialSelection
					loading={isLoadingFiles || partialRevision.isLoadingReports}
					disabled={partialRevision.isPatched}
					reports={partialRevision.reports}
					assignments={partialRevision.assignments}
					selectedFileIds={partialRevision.selectedFileIds}
					onSelectFiles={partialRevision.handleSelectFiles}
					onSaveAssignments={partialRevision.handleSaveAssignments}
					onRemoveAssignment={partialRevision.handleRemoveAssignment}
				/>
			);
		}

		return <FilesSelection isLoading={isLoadingFiles} />;
	}

	const mainSteps = [
		{
			component: (
				<Informations disabled={isRevision} companyLocked={!isLegacyVersion} onValueChange={handleValueChange} />
			)
		},
		{
			component: buildFileSelectionStep()
		}
	];

	if (isError || errorGettingCustomReport || errorGettingBerafiles) {
		return (
			<Row justify="center">
				<Col span={24}>
					<span>{I18n.get('Oops... Something went wrong!')}</span>
				</Col>
			</Row>
		);
	}

	return (
		<Col xs={24}>
			<HeaderRow justify="center">
				<Header />
			</HeaderRow>
			<Row justify="center">{showBeraReportStep()}</Row>
			<FooterRow justify="center">
				<Footer
					currentStep={step}
					loading={isLoading}
					onNextClick={handleNextStep}
					totalSteps={mainSteps.length}
					disabled={isNextButtonDisabled()}
					onPreviousClick={handlePreviousStep}
				/>
			</FooterRow>
			{isMissingRevisionType && (
				<ModalRevision
					open
					report={{ id: jobSummary.id }}
					description={REVISION_TYPE_DESCRIPTION}
					onSelectType={handleSelectRevisionType}
					onCancel={handleCloseRevisionType}
				/>
			)}
			{!!revisionDetection && (
				<ModalRevision
					open
					report={revisionDetection.source}
					overrides={revisionDetection.overrides}
					description={REVISION_DETECTED_DESCRIPTION}
					onCancel={handleCloseRevisionDetection}
				/>
			)}
		</Col>
	);
}
